import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon, UserPlusIcon, TrashIcon, StarIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { apiRequest } from '../../utils/apiUtils';

const ManageTeamMembersModal = ({ team, onClose, onMembersUpdated }) => {
  const [teamDetails, setTeamDetails] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);

  const fetchTeamDetails = useCallback(async () => {
    try {
      const response = await apiRequest(`/teams/${team.id}`);
      if (response.success) {
        setTeamDetails(response.data);
      } else {
        setError(response.error || 'Failed to load team details');
      }
    } catch (err) {
      setError(err.message || 'Failed to load team details');
    }
  }, [team.id]);

  useEffect(() => {
    fetchTeamDetails();
    fetchAvailableUsers();
  }, [team.id, fetchTeamDetails]);

  const fetchAvailableUsers = async () => {
    try {
      setLoading(true);
      const response = await apiRequest('/profiles');
      if (response.success) {
        setAvailableUsers(response.data || []);
      }
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!selectedUserId) return;

    setAdding(true);
    setError(null);

    try {
      const response = await apiRequest(`/teams/${team.id}/members`, {
        method: 'POST',
        data: { user_id: selectedUserId, role: 'member' }
      });

      if (response.success) {
        setSelectedUserId('');
        await fetchTeamDetails();
        // Fetch updated team details to pass to parent
        const teamResponse = await apiRequest(`/teams/${team.id}`);
        if (teamResponse.success && onMembersUpdated) {
          onMembersUpdated(teamResponse.data);
        }
      } else {
        setError(response.error || 'Failed to add member');
      }
    } catch (err) {
      setError(err.message || 'Failed to add member');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!memberToDelete) return;

    try {
      const response = await apiRequest(`/teams/${team.id}/members/${memberToDelete.user_id}`, {
        method: 'DELETE'
      });

      if (response.success) {
        await fetchTeamDetails();
        // Fetch updated team details to pass to parent
        const teamResponse = await apiRequest(`/teams/${team.id}`);
        if (teamResponse.success && onMembersUpdated) {
          onMembersUpdated(teamResponse.data);
        }
        setShowDeleteModal(false);
        setMemberToDelete(null);
      } else {
        setError(response.error || 'Failed to remove member');
      }
    } catch (err) {
      setError(err.message || 'Failed to remove member');
    }
  };

  const handleToggleLead = async (member) => {
    const newRole = member.role === 'lead' ? 'member' : 'lead';

    try {
      const response = await apiRequest(`/teams/${team.id}/members/${member.user_id}`, {
        method: 'PUT',
        data: { role: newRole }
      });

      if (response.success) {
        await fetchTeamDetails();
        // Fetch updated team details to pass to parent
        const teamResponse = await apiRequest(`/teams/${team.id}`);
        if (teamResponse.success && onMembersUpdated) {
          onMembersUpdated(teamResponse.data);
        }
      } else {
        setError(response.error || 'Failed to update member role');
      }
    } catch (err) {
      setError(err.message || 'Failed to update member role');
    }
  };

  // Get users who are not already in the team
  const getNonMembers = () => {
    if (!teamDetails || !teamDetails.members) return availableUsers;
    const memberIds = new Set(teamDetails.members.map(m => m.user_id));
    return availableUsers.filter(user => !memberIds.has(user.id));
  };

  const nonMembers = getNonMembers();

  return (
    <div className="fixed z-50 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-secondary-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>

        {/* Center modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              onClick={onClose}
              className="bg-white rounded-md text-secondary-400 hover:text-secondary-500 focus:outline-none"
            >
              <span className="sr-only">Close</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="w-full mt-3 text-center sm:mt-0 sm:text-left">
              <h3 className="text-lg leading-6 font-medium text-secondary-900" id="modal-title">
                Manage Team Members
              </h3>
              <p className="mt-2 text-sm text-secondary-500">
                Team: <span className="font-medium text-secondary-700">{team.name}</span>
              </p>

              {error && (
                <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
                  {error}
                </div>
              )}

              {/* Add Member Form */}
              <form onSubmit={handleAddMember} className="mt-6">
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Add Member
                </label>
                <div className="flex space-x-2">
                  <select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    className="flex-1 block border border-secondary-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    disabled={adding || loading}
                  >
                    <option value="">Select a user...</option>
                    {nonMembers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name} ({user.email})
                      </option>
                    ))}
                  </select>
                  <button
                    type="submit"
                    disabled={!selectedUserId || adding}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <UserPlusIcon className="h-5 w-5 mr-1" />
                    {adding ? 'Adding...' : 'Add'}
                  </button>
                </div>
              </form>

              {/* Current Members List */}
              <div className="mt-6">
                <h4 className="text-sm font-medium text-secondary-700 mb-3">
                  Current Members ({teamDetails?.members?.length || 0})
                </h4>

                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  </div>
                ) : teamDetails?.members?.length === 0 ? (
                  <div className="text-center py-8 bg-secondary-50 rounded-lg">
                    <p className="text-sm text-secondary-500">No members yet. Add someone to get started.</p>
                  </div>
                ) : (
                  <div className="border border-secondary-200 rounded-lg divide-y divide-secondary-200">
                    {teamDetails?.members?.map((member) => (
                      <div key={member.id} className="flex items-center justify-between p-4 hover:bg-secondary-50">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-primary-600 font-medium text-sm">
                              {member.profiles?.first_name?.charAt(0)}{member.profiles?.last_name?.charAt(0)}
                            </span>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-secondary-900">
                              {member.profiles?.first_name} {member.profiles?.last_name}
                              {member.role === 'lead' && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                  <StarIconSolid className="h-3 w-3 mr-1" />
                                  Lead
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-secondary-500">{member.profiles?.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleToggleLead(member)}
                            className={`p-1 rounded ${
                              member.role === 'lead'
                                ? 'text-yellow-600 hover:text-yellow-700'
                                : 'text-secondary-400 hover:text-yellow-600'
                            }`}
                            title={member.role === 'lead' ? 'Remove lead badge' : 'Make team lead'}
                          >
                            {member.role === 'lead' ? (
                              <StarIconSolid className="h-5 w-5" />
                            ) : (
                              <StarIcon className="h-5 w-5" />
                            )}
                          </button>
                          <button
                            onClick={() => {
                              setMemberToDelete(member);
                              setShowDeleteModal(true);
                            }}
                            className="p-1 text-secondary-400 hover:text-red-600"
                            title="Remove from team"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Close Button */}
              <div className="mt-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full inline-flex justify-center rounded-md border border-secondary-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-secondary-700 hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:text-sm"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Member Confirmation Modal */}
      {showDeleteModal && memberToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowDeleteModal(false)}>
              <div className="absolute inset-0 bg-secondary-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-secondary-900">Remove Team Member</h3>
                    <div className="mt-2">
                      <p className="text-sm text-secondary-500">
                        Are you sure you want to remove <strong>{memberToDelete.profiles?.first_name} {memberToDelete.profiles?.last_name}</strong> from the team?
                        They will no longer have access to team-related issues and assignments.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-secondary-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleRemoveMember}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  <TrashIcon className="h-4 w-4 mr-2" />
                  Remove Member
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setMemberToDelete(null);
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-secondary-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-secondary-700 hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageTeamMembersModal;
